import { ColumnDef } from "@tanstack/react-table";
import {
  SegmentTableData,
  SegmentType,
  SegmentTypeOption,
  SegmentColumnOptions,
  SegmentTypeConfigItem,
} from "../types/segments.types";
import { getDateTimeByFormat } from "../../../../../redux/slices/dateTimeFormatSlice";
import { Avatar } from "../../../../../components/Avatar/Avatar";
import { TruncatedTextTooltip } from "../../../../../components/TruncatedTextTooltip/TruncatedTextTooltip";
import UserManagementIcon from "../../../../../assets/svg/user-management-icon";
import MarketingConsentIcon from "../../../../../assets/svg/marketing-consent-icon";
import LeadStageIcon from "../../../../../assets/svg/lead-stage-icon";
import ChevronRightIcon from "../../../../../assets/svg/chevron-right-icon";
import LocationIcon from "../../../../../assets/svg/location-icon";
import FlagIconCDN from "../../../../../components/FlagIconCDN/FlagIconCDN";
import SegmentTypeBadge from "../components/SegmentTypeBadge";
import {
  SortableColumnHeader,
  FilterableColumnHeader,
} from "../components/ColumnHeader";
import SegmentTypeFilter from "../components/SegmentTypeFilter";
import { ContactOwnerFilter } from "../../../../Crm/components/KanbanGrid/components/tableFilters/ContactOwnerFilter";
import { Lead } from "../../../../Crm/utils/types";
import { getClientCRMFlag } from "../../../../../utils/constants/clientCustomCRMFlags";
import CalendarIcon from "../../../../../assets/svg/calendar-icon";
import PoundIcon from "../../../../../assets/svg/pound-icon";
import { getDealCurrencyIconByRegion } from "../../../../Crm/components/Deals/utils/functions";
import HomeIcon from "../../../../../assets/svg/home-icon";
import RoomTypeIcon from "../../../../../assets/svg/room-type-icon";
import UniversityIcon from "../../../../../assets/svg/university-icon";
import AvailabilityIcon from "../../../../../assets/svg/availability-icon";
import ClockIcon from "../../../../../assets/svg/clock-icon";
import VerifiedIcon from "../../../../../assets/svg/VerifiedIcon";
import ProfileTwoUser from "../../../../../assets/svg/v1/profile-two-user";
import NetworkIcon from "../../../../../assets/svg/network-icon";
import type { FilterFieldOption } from "../../../../Crm/components/HelpDesk/components/CreateView/utils/constants";
import { currencyOptions } from "../../../../../utils/constants/globalConstants";

export const SEGMENT_TYPE_CONFIG: Record<SegmentType, SegmentTypeConfigItem> = {
  static: {
    label: "Static",
    dotColor: "bg-[rgba(247,104,8,0.8)]",
    description:
      "A fixed snapshot of contacts captured at the time of creation. Members don't change unless updated manually.",
  },
  dynamic: {
    label: "Dynamic / Active",
    dotColor: "bg-[rgba(0,163,42,0.8)]",
    description:
      "A live list that auto-updates as contacts meet or stop meeting the criteria.",
  },
};

export const SEGMENT_TYPE_OPTIONS: SegmentTypeOption[] = [
  { value: "all", label: "All Types" },
  {
    value: "static",
    label: SEGMENT_TYPE_CONFIG.static.label,
    dotColor: SEGMENT_TYPE_CONFIG.static.dotColor,
  },
  {
    value: "dynamic",
    label: SEGMENT_TYPE_CONFIG.dynamic.label,
    dotColor: SEGMENT_TYPE_CONFIG.dynamic.dotColor,
  },
];

export const EXPORT_FORMAT_OPTIONS = [
  { value: "csv", label: "CSV", iconKey: "file.csv" as const },
  { value: "xls", label: "XLS", iconKey: "file.xls" as const },
  { value: "xlsx", label: "XLSX", iconKey: "file.xlsx" as const },
] as const;

export const EXPORT_NAME_TRUNCATE_LEN = 42;

export const SEGMENT_DEFAULT_ROWS_PER_PAGE = 10;
export const SEGMENT_TABLE_STAGE_ID = "segment-contacts";
export const SEGMENT_TABLE_BASE_COLOR = "#285BD5";

export const SEGMENT_STAGE_COLOR_MAP: Record<string, string> = {
  [SEGMENT_TABLE_STAGE_ID]: "#6141E1",
  enquiry: "#6141E1",
  qualified: "#285BD5",
  application: "#A78BFA",
  deposit: "#22C55E",
};

export const SEGMENT_STAGE_NAME_MAP: Record<string, string> = {
  [SEGMENT_TABLE_STAGE_ID]: "ALL",
  enquiry: "ENQUIRY",
  qualified: "QUALIFIED",
  application: "APPLICATION",
  deposit: "DEPOSIT",
};

export const SEGMENT_API_SORT_KEY_MAP: Record<string, string> = {
  name: "name",
  size: "size",
  last_updated: "updated_at",
  created_on: "created_at",
};

export const SEGMENT_NAME_MAX_LENGTH = 200;

export const SEGMENT_POPUP_POSITION = {
  HALF_WIDTH: 100,
  MAX_RIGHT: 205,
  RIGHT_PADDING: 215,
  MIN_LEFT: 10,
  OFFSET_Y: 5,
} as const;

export const SEGMENT_STRINGS = {
  allSegments: "All Segments",
  searchPlaceholder: "Search",
  segmentsTableAriaLabel: "Segments table",
  useThisSegment: "Use this segment",
  actionsAriaLabel: "Segment actions",
  preview: "Preview",
  editSegment: "Edit Segment",
  deleteSegment: "Delete Segment",
  segmentNotFound: "Segment not found",
  allContacts: "All Contacts",
  searchInSegment: "Search in segment",
  exportSegment: "Export Segment",
  exportSegmentHeader: "Export Segment",
  exportNameLabel: "Export name",
  exportNamePlaceholder: "Enter export name",
  fileFormatLabel: "File format",
  cancel: "Cancel",
  export: "Export",
  closeAriaLabel: "Close",
  closePreviewAriaLabel: "Close preview",
  segmentPreview: "Segment Preview",
  segmentFallbackName: "Segment",
  deleteSegmentTitle: "Delete Segment",
  deleteSegmentConfirmPrefix: 'Are you sure you want to delete the segment "',
  deleteSegmentConfirmSuffix: '"?',
  createNewSegment: "Create New Segment",
  segmentDescription:
    "Group your contacts into targeted audiences to power smarter, personalized WorkFlos by shared properties or behaviors.",
  segmentNameLabel: "Segment Name",
  segmentNamePlaceholder: "Enter segment name",
  segmentTypeLabel: "Segment Type",
  addFilters: "Add Filters",
  maxCharactersReached: "Max 200 characters limit reached",
  filtersTitle: "Filters",
  editFilters: "Edit",
  noFiltersApplied: "No filters applied",
  statsSize: "Size (Contacts)",
  statsType: "Type",
  statsPercentOfDb: "% of database (Contacts)",
  statsCreatedOn: "Created on",
  statsCreatedBy: "Created by",
  toast: {
    createSuccess: "Segment created successfully",
    updateSuccess: "Your segments are being updated. Please wait",
    filtersSaved: "Segment created successfully",
    exportSuccess: "Segment exported successfully",
    createFailed: "Segment Creation Failed",
    updateFailed: "Segment Update Failed",
    filtersSaveFailed: "Segment Filters Save Failed",
    exportFailed: "Segment Export Failed",
    deleteSuccess: "Segment deleted successfully",
    deleteFailed: "Segment Deletion Failed",
    linkFailed: "Segment Link Failed",
  },
  emptyStateTitle: "No segments yet, Create New Segment",
  emptyStateDescription:
    "Group your contacts into targeted audiences to power smarter, personalized WorkFlos by shared properties or behaviors.",
} as const;

const DEAL_DATE_FIELD_KEYS = [
  "deal_contract_start",
  "deal_contract_end",
  "deal_tenancy_sent_out",
  "deal_tenancy_created",
  "deal_tenancy_completed",
  "deal_signature_deadline",
  "deal_cancellation_effective_date",
  "deal_cancellation_initiated_date",
  "deal_pending_cancellation_date",
  "deal_date_entered_cancelled",
  "deal_contract_completed_date",
  "deal_close_date",
  "deal_created_at",
  "deal_updated_at",
] as const;

export const SEGMENT_DEAL_DATE_FIELDS: string[] = [...DEAL_DATE_FIELD_KEYS];

export const SEGMENT_DEAL_NUMBER_FIELDS = [
  "deal_total_amount",
  "deal_weekly_rent",
  "deal_contract_weeks",
  "deal_days_in_current_stage",
];

export const SEGMENT_DEAL_BOOLEAN_FIELDS = ["deal_guarantor_signed"];

export const SEGMENT_DEAL_FREE_TEXT_FIELDS = [
  "deal_room_number",
  "how_did_you_hear_about_us",
];

// Lead-side enumerable STRING fields per catalogue.
// (UI: single dropdown for single ops, multi-select for `not_in`.)
// `current_stage` is intentionally NOT included so its existing multi-select
// stage-pill picker stays as-is.
export const SEGMENT_LEAD_STRING_FIELDS = ["academic_year"];

// Lead-side ARRAY-typed fields per catalogue. Always send `string[]` (or
// `number[]` after coercion) regardless of operator.
export const SEGMENT_LEAD_ARRAY_FIELDS = [
  "source_country",
  "destination_country",
  "destination_city",
  "assigned_agents",
  "level_of_study",
  "room_type",
  "utm_source",
  "utm_medium",
  "attribution_channel",
  "standard_medium",
  "standard_source",
  "device_type",
  "property",
  "tags",
  "investor",
  "current_stage",
];

export const SEGMENT_DEAL_ARRAY_FIELDS = [
  "deal_assignee",
  "deal_stage",
  "deal_property_name",
  "deal_room_type",
  "deal_city",
  "deal_availability_title",
  "deal_academic_year",
  "deal_currency",
];

export const SEGMENT_EXTRA_FILTER_FIELDS: FilterFieldOption[] = [
  {
    key: "marketing_consent",
    label: "Marketing Consent",
    icon: MarketingConsentIcon,
    iconClassName: "w-[14px] h-[14px]",
    section: "lead",
  },
  {
    key: "how_did_you_hear_about_us",
    label: "How did you hear about us",
    icon: NetworkIcon,
    section: "lead",
  },
  {
    key: "current_stage",
    label: "Lead Stage",
    icon: LeadStageIcon,
    insertAfterKey: "level_of_study",
    section: "lead",
  },
  // ---- BOOKING fields (object_type stays "deal" on the wire) ----
  {
    key: "deal_assignee",
    label: "Booking Owner",
    icon: ProfileTwoUser,
    section: "deal",
  },
  {
    key: "deal_stage",
    label: "Booking Stage",
    icon: LeadStageIcon,
    section: "deal",
  },
  {
    key: "deal_total_amount",
    label: "Booking Amount",
    icon: PoundIcon,
    section: "deal",
  },
  {
    key: "deal_weekly_rent",
    label: "Booking Weekly Rent",
    icon: PoundIcon,
    section: "deal",
  },
  {
    key: "deal_contract_weeks",
    label: "Contract Weeks",
    icon: ClockIcon,
    iconClassName: "!w-[14px] !h-[14px]",
    section: "deal",
  },
  {
    key: "deal_days_in_current_stage",
    label: "Days In Current Stage",
    icon: ClockIcon,
    iconClassName: "!w-[14px] !h-[14px]",
    section: "deal",
  },
  {
    key: "deal_contract_start",
    label: "Contract Start Date",
    icon: CalendarIcon,
    section: "deal",
  },
  {
    key: "deal_contract_end",
    label: "Contract End Date",
    icon: CalendarIcon,
    section: "deal",
  },
  {
    key: "deal_tenancy_sent_out",
    label: "Tenancy Sent Out",
    icon: CalendarIcon,
    section: "deal",
  },
  {
    key: "deal_tenancy_created",
    label: "Tenancy Created",
    icon: CalendarIcon,
    section: "deal",
  },
  {
    key: "deal_tenancy_completed",
    label: "Tenancy Completed",
    icon: CalendarIcon,
    section: "deal",
  },
  {
    key: "deal_signature_deadline",
    label: "Signature Deadline",
    icon: CalendarIcon,
    section: "deal",
  },
  {
    key: "deal_cancellation_effective_date",
    label: "Cancellation Effective Date",
    icon: CalendarIcon,
    section: "deal",
  },
  {
    key: "deal_cancellation_initiated_date",
    label: "Cancellation Initiated Date",
    icon: CalendarIcon,
    section: "deal",
  },
  {
    key: "deal_pending_cancellation_date",
    label: "Pending Cancellation Date",
    icon: CalendarIcon,
    section: "deal",
  },
  {
    key: "deal_date_entered_cancelled",
    label: "Date Entered Cancelled",
    icon: CalendarIcon,
    section: "deal",
  },
  {
    key: "deal_contract_completed_date",
    label: "Contract Completed Date",
    icon: CalendarIcon,
    section: "deal",
  },
  {
    key: "deal_close_date",
    label: "Close Date",
    icon: CalendarIcon,
    section: "deal",
  },
  {
    key: "deal_created_at",
    label: "Booking Created Date",
    icon: CalendarIcon,
    section: "deal",
  },
  {
    key: "deal_updated_at",
    label: "Last Activity Date",
    icon: CalendarIcon,
    section: "deal",
  },
  {
    key: "deal_property_name",
    label: "Property",
    icon: HomeIcon,
    section: "deal",
  },
  {
    key: "deal_room_type",
    label: "Room Type",
    icon: RoomTypeIcon,
    section: "deal",
  },
  {
    key: "deal_room_number",
    label: "Room Number",
    icon: RoomTypeIcon,
    section: "deal",
  },
  {
    key: "deal_academic_year",
    label: "Academic Year",
    icon: UniversityIcon,
    section: "deal",
  },
  {
    key: "deal_availability_title",
    label: "Availability Title",
    icon: AvailabilityIcon,
    section: "deal",
  },
  { key: "deal_city", label: "City", icon: LocationIcon, section: "deal" },
  {
    key: "deal_guarantor_signed",
    label: "Guarantor Signed",
    icon: VerifiedIcon,
    section: "deal",
  },
];

const CURRENCY_ICON_FIELD_KEYS = ["deal_total_amount", "deal_weekly_rent"];

export const getSegmentExtraFilterFields = (
  region?: string
): FilterFieldOption[] => {
  const icon = getDealCurrencyIconByRegion(region);
  return SEGMENT_EXTRA_FILTER_FIELDS.map((f) =>
    CURRENCY_ICON_FIELD_KEYS.includes(f.key) ? { ...f, icon } : f
  );
};

// Shared operator catalogues used inside Segments only (Lead + Deal).
// CRM Kanban views keep their original operator labels via CRM_FILTER_FIELD_OPERATORS.

const SEGMENT_STRING_OPS = [
  { key: "equals", label: "Is" },
  { key: "not_equals", label: "Is not" },
  { key: "contains", label: "Contains" },
  { key: "not_contains", label: "Excludes" },
  { key: "contains_any", label: "Any of" },
  { key: "not_in", label: "None of" },
  { key: "exists", label: "Exists" },
];

// Catalogue: array ops = contains, not_contains, equals, exists, not_in, contains_any
// (note: `not_equals` is intentionally NOT included — only `string ops` allows it).
const SEGMENT_ARRAY_OPS = [
  { key: "equals", label: "Is" },
  { key: "contains", label: "Contains" },
  { key: "not_contains", label: "Excludes" },
  { key: "contains_any", label: "Any of" },
  { key: "not_in", label: "None of" },
  { key: "exists", label: "Exists" },
];

const SEGMENT_DATE_OPS = [
  { key: "equals", label: "Is" },
  { key: "before", label: "Before" },
  { key: "after", label: "After" },
  { key: "range", label: "Between" },
  { key: "exists", label: "Exists" },
];

const SEGMENT_NUMBER_OPS = [
  { key: "equals", label: "Is" },
  { key: "not_equals", label: "Is not" },
  { key: "greater_than", label: "Greater than" },
  { key: "less_than", label: "Less than" },
  { key: "range", label: "Between" },
  { key: "exists", label: "Exists" },
];

const SEGMENT_BOOLEAN_OPS = [
  { key: "equals", label: "Is" },
  { key: "exists", label: "Exists" },
];

const SEGMENT_RANGE_ONLY_OPS = [{ key: "range", label: "Between" }];

const buildDealOperators = (): Record<
  string,
  { key: string; label: string }[]
> => {
  const operators: Record<string, { key: string; label: string }[]> = {
    // catalogue lists these as STRING but they have enumerable values, so we
    // give them string ops; UI renders a single-select dropdown.
    deal_stage: SEGMENT_STRING_OPS,
    deal_assignee: SEGMENT_STRING_OPS,
    deal_currency: SEGMENT_STRING_OPS,
    deal_property_name: SEGMENT_STRING_OPS,
    deal_room_type: SEGMENT_STRING_OPS,
    deal_room_number: SEGMENT_STRING_OPS,
    deal_city: SEGMENT_STRING_OPS,
    deal_availability_title: SEGMENT_STRING_OPS,
    deal_academic_year: SEGMENT_STRING_OPS,
    deal_total_amount: SEGMENT_NUMBER_OPS,
    deal_weekly_rent: SEGMENT_NUMBER_OPS,
    deal_contract_weeks: SEGMENT_NUMBER_OPS,
    deal_days_in_current_stage: SEGMENT_NUMBER_OPS,
    deal_guarantor_signed: SEGMENT_BOOLEAN_OPS,
  };
  SEGMENT_DEAL_DATE_FIELDS.forEach((key) => {
    operators[key] = SEGMENT_DATE_OPS;
  });
  return operators;
};

// Per spec, lead-side filters in Segments expose the full operator catalogue
// with the unified labels. CRM Kanban views are unaffected because they don't
// pass these extras through.
const buildLeadOperators = (): Record<
  string,
  { key: string; label: string }[]
> => ({
  duration: [
    { key: "equals", label: "Is" },
    { key: "range", label: "Between" },
  ],
  // STRING (catalogue): equals, contains, not_contains, not_equals, not_in, exists
  communication_type: SEGMENT_STRING_OPS,
  university: SEGMENT_STRING_OPS,
  lead_type: SEGMENT_STRING_OPS,
  utm_workflow: SEGMENT_STRING_OPS,
  current_stage: SEGMENT_STRING_OPS,
  academic_year: SEGMENT_STRING_OPS,
  how_did_you_hear_about_us: SEGMENT_STRING_OPS,
  // ARRAY (catalogue): contains, not_contains, equals, exists, not_in, contains_any
  level_of_study: SEGMENT_ARRAY_OPS,
  source_country: SEGMENT_ARRAY_OPS,
  destination_country: SEGMENT_ARRAY_OPS,
  destination_city: SEGMENT_ARRAY_OPS,
  assigned_agents: SEGMENT_ARRAY_OPS,
  tags: SEGMENT_ARRAY_OPS,
  room_type: SEGMENT_ARRAY_OPS,
  property: SEGMENT_ARRAY_OPS.filter((operator) => operator.key !== "equals"),
  utm_source: SEGMENT_ARRAY_OPS,
  utm_medium: SEGMENT_ARRAY_OPS,
  attribution_channel: SEGMENT_ARRAY_OPS,
  standard_medium: SEGMENT_ARRAY_OPS,
  standard_source: SEGMENT_ARRAY_OPS,
  device_type: SEGMENT_ARRAY_OPS,
  investor: SEGMENT_ARRAY_OPS,
  // BOOLEAN
  viewings: SEGMENT_BOOLEAN_OPS,
  exclude_repeat_leads: SEGMENT_BOOLEAN_OPS,
  // RANGE
  budget: SEGMENT_RANGE_ONLY_OPS,
  lease_duration: SEGMENT_RANGE_ONLY_OPS,
});

export const SEGMENT_EXTRA_OPERATORS: Record<
  string,
  { key: string; label: string }[]
> = {
  marketing_consent: SEGMENT_BOOLEAN_OPS,
  ...buildLeadOperators(),
  ...buildDealOperators(),
};

const buildCurrencyOptions = (): { key: string; label: string }[] =>
  Object.entries(currencyOptions).map(([code, symbol]) => ({
    key: code,
    label: `${code} (${symbol})`,
  }));

export const SEGMENT_EXTRA_VALUE_OPTIONS: Record<
  string,
  { key: string; label: string }[]
> = {
  marketing_consent: [
    { key: "true", label: "Yes" },
    { key: "false", label: "No" },
  ],
  deal_guarantor_signed: [
    { key: "true", label: "Yes" },
    { key: "false", label: "No" },
  ],
  deal_currency: buildCurrencyOptions(),
};

export const SEGMENT_EXTRA_SINGLE_VALUE_FIELDS = [
  "marketing_consent",
  "deal_guarantor_signed",
  "deal_room_number",
  ...SEGMENT_DEAL_DATE_FIELDS,
  ...SEGMENT_DEAL_NUMBER_FIELDS,
];

// Fields that render a free-text box on `contains` / `not_contains` in the
// segment filter UI (and send a single string) instead of a dropdown. Only
// fields whose stored value is a typeable string (a name or free-form text)
// belong here — never ID/code/enum fields like countries, cities, agents,
// tags or stages.
export const SEGMENT_FREE_TEXT_FIELDS = [
  "deal_city",
  "deal_availability_title",
  "deal_property_name",
  "utm_source",
  "utm_medium",
];

export const createSegmentColumns = (
  opts: SegmentColumnOptions
): ColumnDef<SegmentTableData>[] => [
  {
    accessorKey: "name",
    minSize: 300,
    header: () => (
      <SortableColumnHeader
        label="Name"
        sortField="name"
        activeSortKey={opts.activeSortKey}
        activeSortOrder={opts.activeSortOrder}
        onSort={opts.onSort}
        onTogglePopper={opts.onToggleSortPopper}
        isPopperOpen={opts.activeSortPopper === "name"}
      />
    ),
    cell: ({ row }) => (
      <div className="overflow-hidden">
        <TruncatedTextTooltip
          text={row.getValue("name")}
          maxWidth="max-w-[280px]"
          className="text-sm font-normal text-verba-indigo dark:text-verba-indigo-dk"
        />
      </div>
    ),
  },
  {
    accessorKey: "size",
    size: 160,
    header: () => (
      <SortableColumnHeader
        label={SEGMENT_STRINGS.statsSize}
        sortField="size"
        activeSortKey={opts.activeSortKey}
        activeSortOrder={opts.activeSortOrder}
        onSort={opts.onSort}
        onTogglePopper={opts.onToggleSortPopper}
        isPopperOpen={opts.activeSortPopper === "size"}
      />
    ),
    cell: ({ row }) => {
      const size = row.getValue("size") as number | null;
      return (
        <div className="flex items-center gap-[4px] overflow-hidden">
          <UserManagementIcon className="w-[14px] h-[14px] text-verba-indigo dark:text-verba-indigo-dk shrink-0" />
          <p className="text-sm font-normal text-verba-indigo dark:text-verba-indigo-dk truncate">
            {size ?? 0}
          </p>
        </div>
      );
    },
  },
  {
    accessorKey: "type",
    size: 120,
    header: () => (
      <FilterableColumnHeader
        label="Type"
        filterKey="type"
        activeFilter={opts.activeFilterColumn}
        onToggleFilter={opts.onToggleFilter}
      >
        {(triggerRef) => (
          <SegmentTypeFilter
            selectedType={opts.selectedTypeFilter}
            onSelect={opts.onTypeFilterSelect}
            onClose={opts.onCloseFilter}
            triggerRef={triggerRef}
          />
        )}
      </FilterableColumnHeader>
    ),
    cell: ({ row }) => (
      <div className="overflow-hidden">
        <SegmentTypeBadge type={row.getValue("type") as SegmentType} />
      </div>
    ),
  },
  {
    accessorKey: "last_updated",
    size: 220,
    header: () => (
      <SortableColumnHeader
        label="Last Updated (GMT +5:30)"
        sortField="last_updated"
        activeSortKey={opts.activeSortKey}
        activeSortOrder={opts.activeSortOrder}
        onSort={opts.onSort}
        onTogglePopper={opts.onToggleSortPopper}
        isPopperOpen={opts.activeSortPopper === "last_updated"}
      />
    ),
    cell: ({ row }) => {
      const value = row.getValue("last_updated") as string;
      if (!value) return null;
      const dateTime = getDateTimeByFormat(new Date(value));
      return (
        <div className="overflow-hidden">
          <p className="text-sm font-normal text-verba-indigo dark:text-verba-indigo-dk truncate">
            {dateTime.date}, {dateTime.time}
          </p>
        </div>
      );
    },
  },
  {
    accessorKey: "created_by",
    size: 160,
    header: () => (
      <FilterableColumnHeader
        label="Created By"
        filterKey="created_by"
        activeFilter={opts.activeFilterColumn}
        onToggleFilter={opts.onToggleFilter}
      >
        {(triggerRef) => (
          <ContactOwnerFilter
            triggerRef={triggerRef}
            selectedAgentIds={opts.selectedCreatedByIds}
            onApply={opts.onCreatedByApply}
            onReset={opts.onCreatedByReset}
            onClose={opts.onCloseFilter}
            headingText="Created By"
            customAgents={opts.customAgents}
            maxHeight="300px"
          />
        )}
      </FilterableColumnHeader>
    ),
    cell: ({ row }) => {
      const userName = row.getValue("created_by") as string;
      return (
        <div className="flex items-center gap-[5px] overflow-hidden">
          {userName && (
            <Avatar
              text={userName}
              size={18}
              className="p-[2px]! text-[8px]! shrink-0"
            />
          )}
          <TruncatedTextTooltip
            text={userName}
            maxWidth="max-w-full"
            className="text-xs font-medium text-verba-indigo dark:text-verba-indigo-dk"
          />
        </div>
      );
    },
  },
  {
    accessorKey: "created_on",
    size: 180,
    header: () => (
      <SortableColumnHeader
        label="Created On"
        sortField="created_on"
        activeSortKey={opts.activeSortKey}
        activeSortOrder={opts.activeSortOrder}
        onSort={opts.onSort}
        onTogglePopper={opts.onToggleSortPopper}
        isPopperOpen={opts.activeSortPopper === "created_on"}
      />
    ),
    cell: ({ row }) => {
      const value = row.getValue("created_on") as string;
      if (!value) return null;
      const dateTime = getDateTimeByFormat(new Date(value));
      return (
        <div className="overflow-hidden">
          <p className="text-sm font-normal text-verba-indigo dark:text-verba-indigo-dk truncate">
            {dateTime.date}, {dateTime.time}
          </p>
        </div>
      );
    },
  },
];

const shouldUseNeighbourhoodLabel = () =>
  getClientCRMFlag("useNeighbourhoodLabel");

export const getSegmentContactColumns = (
  hideArrow = false,
  stageColorMap: Record<string, string> = {},
  stageNameMap: Record<string, string> = {}
): ColumnDef<Lead>[] => {
  const columns: ColumnDef<Lead>[] = [
    {
      accessorKey: "name",
      minSize: 200,
      header: () => (
        <div className="text-left px-3 py-2">
          <span className="text-md font-medium text-gray-375 dark:text-gray-375-dk">
            Name
          </span>
        </div>
      ),
      cell: ({ row }) => {
        const name = row.original?.name || "Anonymous";
        return (
          <div className="px-3 flex items-center gap-2 max-w-full overflow-hidden">
            <Avatar text={name} size={24} />
            <TruncatedTextTooltip
              text={name}
              className="text-md text-verba-indigo dark:text-verba-indigo-dk font-medium truncate"
            />
          </div>
        );
      },
    },
    {
      accessorKey: "email",
      minSize: 220,
      header: () => (
        <div className="text-left px-3 py-2">
          <span className="text-md font-medium text-gray-375 dark:text-gray-375-dk">
            Email
          </span>
        </div>
      ),
      cell: ({ row }) => (
        <div className="px-3 max-w-full overflow-hidden">
          <TruncatedTextTooltip
            text={row.original?.email || "-"}
            className="text-md text-verba-indigo dark:text-verba-indigo-dk truncate"
          />
        </div>
      ),
    },
    {
      accessorKey: "phone",
      minSize: 160,
      header: () => (
        <div className="text-left px-3 py-2">
          <span className="text-md font-medium text-gray-375 dark:text-gray-375-dk">
            Phone Number
          </span>
        </div>
      ),
      cell: ({ row }) => {
        const code = row.original?.phone_country_code;
        const phone = row.original?.phone;
        const display = phone ? `${code} ${phone}` : "-";
        return (
          <div className="px-3 max-w-full overflow-hidden">
            <span className="text-md text-verba-indigo dark:text-verba-indigo-dk whitespace-nowrap truncate block">
              {display}
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: "agent",
      minSize: 200,
      header: () => (
        <div className="text-left px-3 py-2">
          <span className="text-md font-medium text-gray-375 dark:text-gray-375-dk">
            Contact Owner
          </span>
        </div>
      ),
      cell: ({ row }) => {
        const agents = row.original?.agent;
        if (!Array.isArray(agents) || agents.length === 0) {
          return (
            <div className="px-3">
              <span className="text-md text-verba-indigo dark:text-verba-indigo-dk">
                -
              </span>
            </div>
          );
        }
        const first = agents[0];
        return (
          <div className="px-3 flex items-center gap-2 max-w-full overflow-hidden">
            <Avatar text={first.name} size={24} />
            <TruncatedTextTooltip
              text={first.name}
              className="text-md text-verba-indigo dark:text-verba-indigo-dk truncate"
            />
            {agents.length > 1 && (
              <span className="text-xs text-gray-placeholder dark:text-gray-placeholder-dk shrink-0">
                +{agents.length - 1}
              </span>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "current_stage",
      minSize: 150,
      header: () => (
        <div className="text-left py-2">
          <span className="text-md font-medium text-gray-375 dark:text-gray-375-dk">
            Lead Status
          </span>
        </div>
      ),
      cell: ({ row }) => {
        const stage = row.original?.current_stage;
        const color = stageColorMap[stage] || "#6b7280";
        const name = stageNameMap[stage] || stage?.toUpperCase();
        return (
          <div className="py-1 px-1.5 w-fit flex items-center gap-2 border border-border-gray dark:border-border-gray-dk rounded-[6px]">
            <span
              className="w-2 h-2 rounded-full shrink-0"
              style={{ backgroundColor: color }}
            />
            <span className="text-md uppercase font-semibold text-verba-indigo dark:text-verba-indigo-dk whitespace-nowrap">
              {name}
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: "property",
      minSize: 200,
      header: () => (
        <div className="text-left px-3 py-2">
          <span className="text-md font-medium text-gray-375 dark:text-gray-375-dk">
            {shouldUseNeighbourhoodLabel() ? "Neighbourhood" : "Property"}
          </span>
        </div>
      ),
      cell: ({ row }) => {
        const property = row.original?.property;
        const propertyText = Array.isArray(property)
          ? property.join(", ")
          : property;
        return (
          <div className="px-3 max-w-full overflow-hidden">
            <TruncatedTextTooltip
              text={propertyText || "-"}
              className="text-md text-verba-indigo dark:text-verba-indigo-dk truncate"
            />
          </div>
        );
      },
    },
    {
      accessorKey: "created_at",
      minSize: 160,
      header: () => (
        <div className="text-left px-3 py-2">
          <span className="text-md font-medium text-gray-375 dark:text-gray-375-dk">
            Created Date
          </span>
        </div>
      ),
      cell: ({ row }) => {
        const createdAt = row.original?.created_at;
        if (!createdAt) return <div className="px-3">-</div>;
        const { date, time } = getDateTimeByFormat(new Date(createdAt));
        return (
          <div className="px-3">
            <span className="text-md text-verba-indigo dark:text-verba-indigo-dk whitespace-nowrap">
              {date} {time}
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: "updated_at",
      minSize: 160,
      header: () => (
        <div className="text-left px-3 py-2">
          <span className="text-md font-medium text-gray-375 dark:text-gray-375-dk">
            Last Activity
          </span>
        </div>
      ),
      cell: ({ row }) => {
        const updatedAt = row.original?.updated_at;
        if (!updatedAt) return <div className="px-3">-</div>;
        const { date, time } = getDateTimeByFormat(new Date(updatedAt));
        return (
          <div className="px-3">
            <span className="text-md text-verba-indigo dark:text-verba-indigo-dk whitespace-nowrap">
              {date} {time}
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: "destination_country",
      minSize: 180,
      header: () => (
        <div className="text-left px-3 py-2">
          <span className="text-md font-medium text-gray-375 dark:text-gray-375-dk">
            Destination Country
          </span>
        </div>
      ),
      cell: ({ row }) => {
        const country = row.original?.destination_country;
        const alpha2 = row.original?.destination_country_alpha2;
        return (
          <div className="px-3 flex items-center gap-2 max-w-full overflow-hidden">
            {alpha2 && (
              <div className="w-[18px] h-[14px] shrink-0">
                <FlagIconCDN
                  code={alpha2}
                  flagIconClassName="w-[18px] h-[14px] shrink-0 border border-gray-75 dark:border-gray-75-dk rounded-[2px]"
                />
              </div>
            )}
            <TruncatedTextTooltip
              text={country || "-"}
              className="text-md text-verba-indigo dark:text-verba-indigo-dk truncate"
            />
          </div>
        );
      },
    },
    {
      accessorKey: "destination_city",
      minSize: 150,
      header: () => (
        <div className="text-left px-3 py-2">
          <span className="text-md font-medium text-gray-375 dark:text-gray-375-dk">
            Destination City
          </span>
        </div>
      ),
      cell: ({ row }) => (
        <div className="px-3 max-w-full overflow-hidden flex items-center gap-1">
          {row.original?.destination_city && (
            <LocationIcon className="w-4 h-4 text-gray-placeholder dark:text-gray-placeholder-dk" />
          )}
          <TruncatedTextTooltip
            text={row.original?.destination_city || "-"}
            className="text-md text-verba-indigo dark:text-verba-indigo-dk truncate"
          />
        </div>
      ),
    },
  ];

  if (!hideArrow) {
    columns.push({
      id: "arrow",
      size: 40,
      header: () => <div />,
      cell: () => (
        <div className="flex items-center justify-center px-2">
          <ChevronRightIcon className="w-4 h-4 text-verba-indigo dark:text-verba-indigo-dk" />
        </div>
      ),
    });
  }

  return columns;
};
